import { useCallback, useEffect, useMemo, useState } from 'react'
import ReactPaginate from 'react-paginate'
import { Link } from 'react-router-dom'

import { PageTitle } from '../../../../../_metronic/layout/core'
import { getMatchingPayoutTickets } from '../../core/_requests'
import {
  MatchingPayoutTicket,
  PrepareMatchingPayoutTicketsToObject,
  MatchingFinishedPayoutFilterState,
  IMatchingPayoutFinishedTicketsRequestParams,
  PaginationState,
  matchingCapabilities,
  canUser,
  getVisibleFinishedPayoutColumns,
} from '../../core/_models'
import FinishedPayoutTicketsFilter from './FinishedPayoutTicketsFilter'
import FinishedPayoutTicketItem from './FinishedPayoutTicketItem'
import { IsGranted, useAuth } from '../../../auth'
import { Table } from '../../../../../components/Table'
import { TableHead } from '../../../../../components/Table/TableHead'
import { TableBody } from '../../../../../components/Table/TableBody'

import '../../../../assets/css/pagination.css'
import {
  matchingFinishedPayoutsConfig,
  FinishedPayoutUrlShape,
  urlToFilterState,
  filterStateToUrl,
} from './config'
import { Header } from '../common/Header'
import { Forbidden } from '../common/Forbidden'
import { EMatchingPayoutTicketStatus } from '../../../../../types/enums'
import { convertToUTCByMSK } from '../../../../../utils'
import { parseFiltersFromUrl, serializeFiltersToUrl } from '../../../../../utils/statusFiltersURL'

const { defaultFilters, defaultList, breadcrumbs, perPage, relevantStatusList, urlDefaults, aliasFilters } =
  matchingFinishedPayoutsConfig

const FinishedPayoutTickets = () => {
  const { currentUser } = useAuth()

  // Права и видимость
  const canAccess = useMemo(
    () => canUser(currentUser, matchingCapabilities.canViewFinishedPayouts),
    [currentUser]
  )

  const visibility = useMemo(() => getVisibleFinishedPayoutColumns(currentUser), [currentUser])
  const visibleColumns = useMemo(() => Object.keys(visibility).filter(Boolean), [visibility])
  // Переотправка КБ — отдельное право от matching-прав; список ролей в matchingCapabilities.
  const canResend = useMemo(
    () => IsGranted(matchingCapabilities.canResendCallback, currentUser),
    [currentUser]
  )
  // Тот же список ролей, что у спора о пополнении — бэкенд держит один на весь модуль диспутов.
  const canDispute = useMemo(
    () => IsGranted(matchingCapabilities.canCreateDispute, currentUser),
    [currentUser]
  )
  // Колонка «Действия» общая для КБ и меню спора: шапка, ячейка и colSpan считаются от неё одной.
  const hasActions = canResend || canDispute

  // Фильтры считываются из URL (плоское представление → состояние формы).
  const initialFilters = useMemo(
    () => urlToFilterState(parseFiltersFromUrl<FinishedPayoutUrlShape>(urlDefaults, aliasFilters)),
    []
  )

  // FilterManager читает defaultValues один раз (для инициализации состояния) и использует их же
  // как цель «Сбросить». Чтобы получить и восстановление из URL, и сброс к дефолтам: на первом
  // рендере отдаём значения из URL (сидят состояние), после монтирования — истинные дефолты.
  // Флаг переключаем только когда фильтр реально показан (он под гейтом canAccess, а currentUser
  // может прийти асинхронно) — иначе первый рендер FilterManager не получит значения из URL.
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    if (canAccess) setMounted(true)
  }, [canAccess])
  const filterDefaultValues = mounted ? defaultFilters : initialFilters

  // Состояния
  const [initialized, setInitialized] = useState(false)
  const [tickets, setTickets] = useState(defaultList)
  const [filters, setFilters] = useState<MatchingFinishedPayoutFilterState>(initialFilters)
  const [countPages, setCountPages] = useState(0)
  const [fullCount, setFullCount] = useState(0)
  const [isLoading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [overpaymentCreatedToken, setOverpaymentCreatedToken] = useState<string | null>(null)

  const loadTicketList = useCallback(
    async (query: IMatchingPayoutFinishedTicketsRequestParams) => {
      if (!canAccess) return
      setLoading(true)
      try {
        const response = await getMatchingPayoutTickets(query)
        if (!response?.data?.success) {
          throw new Error(
            'errors' in response?.data ? response?.data?.errors?.general_error : 'Unknown error'
          )
        }

        const listObject = PrepareMatchingPayoutTicketsToObject(response.data.list || [])
        const totalRows = response.data.totalRows || 0
        const pages = Math.ceil(totalRows / perPage)

        setTickets(listObject)
        setCountPages(pages)
        setFullCount(totalRows)
      } catch (error) {
        console.error('Ошибка при загрузке тикетов:', error)
        setTickets({})
        setCountPages(0)
        setFullCount(0)
      } finally {
        setLoading(false)
      }
    },
    [canAccess]
  )

  const applyFilters = useCallback(
    (newFilters: MatchingFinishedPayoutFilterState, pagination?: PaginationState) => {
      if (!canAccess) return
      const mergedFilters = { ...defaultFilters, ...newFilters }
      setFilters({ ...mergedFilters })

      const range = mergedFilters.dateRange
      const from = range?.from
      const to = range?.to
      const safeDateStart = from
        ? convertToUTCByMSK(from instanceof Date ? from : new Date(from))
        : undefined
      const safeDateEnd = to
        ? convertToUTCByMSK(to instanceof Date ? to : new Date(to))
        : undefined

      setCurrentPage(pagination?.page || 1)

      // Разрешаем только статусы завершённых выплат (защита от вручную собранного URL
      // вроде ?statusList[]=CREATED). Пустой выбор трактуем как «оба».
      const selectedStatuses = (mergedFilters.statusList ?? []).filter((s) =>
        relevantStatusList.includes(s as EMatchingPayoutTicketStatus)
      )
      const statusList = (
        selectedStatuses.length ? selectedStatuses : relevantStatusList
      ) as EMatchingPayoutTicketStatus[]

      const query: IMatchingPayoutFinishedTicketsRequestParams = {
        page: pagination?.page || 1,
        perPage: pagination?.perPage || perPage,
        statusList,
        merchantTokenList: mergedFilters.merchantTokenList?.length
          ? mergedFilters.merchantTokenList
          : undefined,
        paymentMethodList: mergedFilters.paymentMethodList?.length
          ? mergedFilters.paymentMethodList
          : undefined,
        search: mergedFilters.search || undefined,
        byCompletionDate: range?.mode === 'completed',
        dateStart: safeDateStart,
        dateEnd: safeDateEnd,
      }

      const urlQuery = serializeFiltersToUrl(filterStateToUrl(mergedFilters), urlDefaults)
      const nextUrl = urlQuery ? `${window.location.pathname}?${urlQuery}` : window.location.pathname
      window.history.replaceState({}, '', nextUrl)

      loadTicketList(query)
    },
    [loadTicketList, canAccess]
  )

  useEffect(() => {
    if (!initialized) {
      applyFilters(initialFilters, { page: 1, perPage })
      setInitialized(true)
    }
  }, [initialized, applyFilters, initialFilters, perPage])

  // Успешное создание переоплаты — показываем верхний alert со ссылкой на
  // компенсационную выплату (она появляется в активных выплатах) и обновляем
  // текущую страницу списка, чтобы кнопка сменилась на статус «в процессе».
  const handleOverpaymentCreated = useCallback(
    (compensationPayoutToken: string) => {
      setOverpaymentCreatedToken(compensationPayoutToken)
      applyFilters(filters, { page: currentPage, perPage })
    },
    [applyFilters, filters, currentPage, perPage]
  )

  // Строка выплаты после спора меняет признак и бейдж — перечитываем текущую страницу.
  const handleDisputeCreated = useCallback(
    () => applyFilters(filters, { page: currentPage, perPage }),
    [applyFilters, filters, currentPage]
  )

  useEffect(() => {
    if (!overpaymentCreatedToken) return
    const timer = setTimeout(() => setOverpaymentCreatedToken(null), 10000)
    return () => clearTimeout(timer)
  }, [overpaymentCreatedToken])

  return (
    <div>
      {overpaymentCreatedToken && (
        <div
          className='alert alert-success position-fixed top-0 start-50 translate-middle-x mt-5 shadow'
          style={{ zIndex: 1100 }}
          role='alert'
        >
          <div className='d-flex align-items-center justify-content-between gap-4'>
            <span>
              Переоплата создана. Компенсационная выплата:{' '}
              <Link to={`/matching/payout/active?ticket=${overpaymentCreatedToken}`}>
                {overpaymentCreatedToken}
              </Link>
            </span>
            <button
              type='button'
              className='btn-close'
              aria-label='Закрыть'
              onClick={() => setOverpaymentCreatedToken(null)}
            />
          </div>
        </div>
      )}
      <div className='card mb-7'>
        <PageTitle breadcrumbs={breadcrumbs}>Завершённые выплаты</PageTitle>
        <Header title='Завершённые выплаты' count={fullCount} />

        {!canAccess && <Forbidden />}

        {canAccess && (
          <FinishedPayoutTicketsFilter
            defaultValues={filterDefaultValues}
            onApplyFilters={applyFilters}
          />
        )}

        {canAccess && (
          <div className='card-body py-3'>
            <Table>
              <TableHead>
                <tr>
                  <th className='min-w-40px max-w-40px align-middle text-center'></th>
                  {visibility.ticketToken && (
                    <th className='min-w-200px'>
                      ID тикета
                      <br />
                      <small>Токен UUID</small>
                    </th>
                  )}
                  {visibility.createdDate && <th className='min-w-100px'>Дата создания</th>}
                  {visibility.paymentMethod && <th className='min-w-120px'>Тип платежа</th>}
                  {visibility.completionDate && <th className='min-w-100px'>Дата закрытия</th>}
                  {visibility.amount && (
                    <th className='min-w-150px'>
                      Сумма
                      <br />
                      <small>Запрос / Факт</small>
                    </th>
                  )}
                  {visibility.user && <th className='min-w-100px align-top'>Клиент</th>}
                  {visibility.merchantId && <th className='min-w-120px align-top'>ID транзакции</th>}
                  {visibility.merchant && <th className='min-w-150px align-top'>Мерчант</th>}
                  {visibility.payoutRequisite && (
                    <th className='min-w-200px align-top'>
                      Реквизит
                      <br />
                      выплаты
                    </th>
                  )}
                  {visibility.status && (
                    <th className='min-w-150px'>
                      Статус
                      <br />
                      тикета
                    </th>
                  )}
                  {hasActions && <th className='min-w-80px text-end'>Действия</th>}
                </tr>
              </TableHead>
              <TableBody>
                {Object.keys(tickets).length > 0 ? (
                  Object.keys(tickets).map((token) => (
                    <FinishedPayoutTicketItem
                      key={token}
                      ticket={tickets[token] as MatchingPayoutTicket}
                      visibleColumns={visibleColumns?.length}
                      visibility={visibility}
                      canResend={canResend}
                      canDispute={canDispute}
                      onDisputeCreated={handleDisputeCreated}
                      onOverpaymentCreated={handleOverpaymentCreated}
                    />
                  ))
                ) : (
                  <tr>
                    <td colSpan={(visibleColumns?.length || 0) + (hasActions ? 1 : 0)} className='text-center'>
                      {isLoading ? 'Загрузка...' : 'Тикеты не найдены'}
                    </td>
                  </tr>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {canAccess && countPages > 1 && (
        <div className='d-flex justify-content-center mt-5'>
          <ReactPaginate
            breakLabel='...'
            nextLabel='Следующая >'
            onPageChange={(selected) =>
              applyFilters({ ...filters }, { page: selected.selected + 1, perPage })
            }
            pageRangeDisplayed={5}
            pageCount={countPages}
            previousLabel='< Предыдущая'
            renderOnZeroPageCount={() => null}
            className='pagination'
            activeClassName='active'
            disabledClassName='disabled'
            nextClassName='page-item'
            nextLinkClassName='page-link'
            pageClassName='page-item'
            pageLinkClassName='page-link'
            previousClassName='page-item'
            previousLinkClassName='page-link'
            breakClassName='page-item'
            breakLinkClassName='page-link'
          />
        </div>
      )}
    </div>
  )
}

export default FinishedPayoutTickets
